/**
 * canvas-engine/renderer/CanvasSectionErrorBoundary.jsx
 *
 * OWN React error boundary for the Canvas Engine.
 *
 * ZERO REUSE: imports NOTHING from `admin-console-ui/src/modules/template-builder/`
 * or `academy-ui/src/template/`. React only.
 *
 * Purpose: a render throw inside one section/element subtree must NOT abort the
 * whole page. This boundary catches such a throw and renders a neutral,
 * self-contained placeholder in place of the failed subtree so SIBLING sections
 * and elements keep rendering. It behaves identically in builder and reader
 * modes (the placeholder carries no selection affordances). (Req 24.3)
 *
 * The boundary fills its positioned box (width:100% / height:100%) so it slots
 * into the same absolute wrapper the renderer would have used for the element,
 * keeping the failure visually contained.
 */

import React from 'react';

const PLACEHOLDER_STYLE = Object.freeze({
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 12,
  background: '#f1f5f9',
  border: '1px dashed #cbd5e1',
  borderRadius: 8,
  color: '#64748b',
  fontSize: 12,
  textAlign: 'center',
  overflow: 'hidden',
});

/**
 * Error boundary that renders a contained neutral placeholder on a child throw.
 *
 * Props:
 *   - children: the subtree to protect.
 *   - label?: optional short text shown in the placeholder (default 'Unavailable').
 *   - fallback?: optional custom node rendered instead of the default placeholder.
 *   - onError?: optional callback invoked with (error, info) when a child throws.
 */
export default class CanvasSectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Switch to the contained placeholder on the next render.
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const { onError } = this.props;
    if (typeof onError === 'function') {
      try {
        onError(error, info);
      } catch (_e) {
        // Never let an error handler itself break the boundary.
      }
    }
  }

  render() {
    const { hasError } = this.state;
    const { children, fallback, label } = this.props;

    if (!hasError) {
      return children;
    }

    if (fallback !== undefined && fallback !== null) {
      return fallback;
    }

    return (
      <div className="ce-error-boundary" style={PLACEHOLDER_STYLE} role="note" aria-label="content unavailable">
        {typeof label === 'string' && label.length > 0 ? label : 'Unavailable'}
      </div>
    );
  }
}
